/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faCreditCard, faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  deleteCreditCard,
  fetchContactInfo,
  fetchServicePlan,
  selectBillingEmail,
  selectBillingError,
  selectBillingLoading,
  selectContactInfo,
  selectContactInfoLoading,
  selectHasValidPromoEngagementDiscount,
  selectPaymentInfo,
  selectServiceAddress,
  selectServicePlan,
} from '../../../redux/slices/billingSlice/billingSlice';

import PlanDetailsCard from './PlanDetailsCard/PlanDetailsCard';
import CancelServiceModal from './modals/CancelServiceModal';
import BillingEmailModal from './modals/BillingEmailModal';
import ServiceAddressModal from './modals/ServiceAddressModal';
import EditPaymentMethodModal from './modals/EditPaymentMethodModal';
import DeletePaymentMethodConfirmationModal from './modals/DeletePaymentMethodConfirmationModal';
import TransactionsTable from './TransactionsTable/TransactionsTable';
import ReconnectPlanPromo from './ReconnectPlanPromo';
import TrialBillingOverview from './TrialBillingOverview';
import { selectSessionInformation } from '../../../redux/slices/sessionSlice';

const BillingOverview = ({ countryList }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const baseUrl = url.endsWith('/') ? url.slice(0, -1) : url;
  const servicePlan = useSelector(selectServicePlan);
  const hasValidPromoEngagement = useSelector(selectHasValidPromoEngagementDiscount);
  const loading = useSelector(selectBillingLoading);
  const contactInfoLoading = useSelector(selectContactInfoLoading);
  const error = useSelector(selectBillingError);
  const contactInfo = useSelector(selectContactInfo);
  const serviceAddress = useSelector(selectServiceAddress);
  const billingEmail = useSelector(selectBillingEmail);
  const paymentInfo = useSelector(selectPaymentInfo);
  const { activeSubscription } = useSelector(selectSessionInformation);

  const [paymentLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showBillingEmailModal, setShowBillingEmailModal] = useState(false);
  const [showServiceAddressModal, setShowServiceAddressModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  // eslint-disable-next-line max-len
  const [showDeletePaymentMethodConfirmationModal, setShowDeletePaymentMethodConfirmationModal] = useState(false);

  const activeTier = servicePlan ? servicePlan.subscriptionTier : null;

  const monthlyAvailable = Boolean(activeTier && activeTier.monthlyCrmProductId)
    && parseFloat(activeTier && activeTier.monthlyPrice) >= 1 && activeTier.active;

  useEffect(() => {
    dispatch(fetchContactInfo());
    dispatch(fetchServicePlan());
  }, [dispatch]);

  const handleChangePlan = () => {
    history.push(`${baseUrl}/change-plan`);
  };
  const handlePrepay = () => {
    history.push(`${baseUrl}/pay-in-advance`);
  };
  const handlePayNow = () => {
    history.push(`${baseUrl}/pay-balance`);
  };
  const handleChangeToMonthly = () => {
    history.push(`${baseUrl}/change-payment-period`);
  };

  const handleOpenCancelModal = () => setShowCancelModal(true);
  const handleCloseCancelModal = () => setShowCancelModal(false);

  const handleConfirmRemovePayment = async () => {
    await dispatch(deleteCreditCard({ subscriptionId: servicePlan.subscriptionId }));
  };

  const handleOpenBillingEmailModal = () => setShowBillingEmailModal(true);
  const handleCloseBillingEmailModal = () => setShowBillingEmailModal(false);

  const handleOpenServiceAddressModal = () => setShowServiceAddressModal(true);
  const handleCloseServiceAddressModal = () => setShowServiceAddressModal(false);

  const handleOpenPaymentMethodModal = () => setShowPaymentMethodModal(true);
  const handleClosePaymentMethodModal = () => setShowPaymentMethodModal(false);

  // eslint-disable-next-line max-len
  const handleOpenDeletePaymentMethodConfirmationModal = () => setShowDeletePaymentMethodConfirmationModal(true);
  // eslint-disable-next-line max-len
  const handleCloseDeletePaymentMethodConfirmationModal = () => setShowDeletePaymentMethodConfirmationModal(false);

  const handleSubscribeFromTrial = () => {
    history.push(`${baseUrl}/change-plan`, { preselectActiveTier: true });
  };

  const shouldShowReconnectPromo = !activeSubscription
    || activeSubscription.subscriptionTierLevel <= 1;

  const formatAddress = (addr) => {
    if (!addr) return '-';

    const line1 = addr.streetLine1 ? addr.streetLine1 : '';
    const line2 = addr.streetLine2 ? addr.streetLine2 : '';
    const city = addr.locality ? addr.locality : '';
    const state = addr.state ? addr.state : '';
    const zip = addr.postalCode ? addr.postalCode : '';
    const country = addr.country ? addr.country : '';

    return `${line1}${line2 ? `, ${line2}` : ''}
      ${city}, ${state} ${zip}
      ${country}`;
  };

  const formatCreditCardInfo = () => {
    if (!contactInfo) return '';
    if (!contactInfo.cardOnFile) return '';
    if (!contactInfo.cardOnFile.cardNumber) return '';
    if (!contactInfo.cardOnFile.expirationDate) return '';
    return `Card ending in ${contactInfo.cardOnFile.cardNumber} Exp ${contactInfo.cardOnFile.expirationDate}`;
  };

  if (loading || contactInfoLoading) {
    return (
      <>
        <div className="container mt-4">
          <ProgressSpinner progressText="Loading..." />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="container mt-4 text-danger">
          Failed to load billing data.
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => dispatch(fetchServicePlan())}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  if (hasValidPromoEngagement) {
    return (
      <TrialBillingOverview
        onComparePlans={handleChangePlan}
        onSubscribe={handleSubscribeFromTrial}
        servicePlan={servicePlan}
      />
    );
  }

  return (
    <>
      {/* <SubscriptionNavTabs activeKey="/s/subscription/billing" /> */}
      <div className="container-fluid px-4 mt-4">
        {shouldShowReconnectPromo ? (
          <ReconnectPlanPromo onComparePlans={handleChangePlan} />
        ) : (
          <div className="row">

            <PlanDetailsCard
              servicePlan={servicePlan}
              paymentInfo={paymentInfo}
              paymentLoading={paymentLoading}
              onChangePlan={handleChangePlan}
              onPrepay={handlePrepay}
              onCancel={handleOpenCancelModal}
              onPayNow={handlePayNow}
              onChangeToMonthly={handleChangeToMonthly}
              monthlyAvailable={monthlyAvailable}
            />

            <div className="col-md-4 mb-4">
              <div className="card">
                <div className="card-header">
                  <strong>Billing & Payment</strong>
                </div>

                <div className="card-body">

                  {/* Service Address */}
                  <div className="mb-3">

                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faBuilding} className="mr-2" />
                        <strong>Service Address</strong>
                      </div>

                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={handleOpenServiceAddressModal}
                      >
                        Edit
                      </button>
                    </div>

                    <div style={{ whiteSpace: 'pre-line' }} className="mt-1">
                      {serviceAddress
                        ? formatAddress(serviceAddress)
                        : 'None specified'}
                    </div>
                  </div>

                  {/* Billing Email */}
                  <div className="mb-3">

                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                        <strong>Billing Email</strong>
                      </div>

                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={handleOpenBillingEmailModal}
                      >
                        Edit
                      </button>
                    </div>

                    <div className="mt-1">
                      {billingEmail || 'None specified'}
                    </div>

                  </div>

                  <div>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faCreditCard} className="mr-2 text-muted" />
                        <strong>Payment Method</strong>
                      </div>

                      <div className="d-flex gap-2">
                        {contactInfo
                        && contactInfo.cardOnFile
                        && contactInfo.cardOnFile.cardNumber ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-link p-0 mr-1"
                              onClick={handleOpenDeletePaymentMethodConfirmationModal}
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={handleOpenPaymentMethodModal}
                            >
                              Edit
                            </button>
                          </>
                          )
                          : (

                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={handleOpenPaymentMethodModal}
                            >
                              Add
                            </button>
                          )}

                      </div>
                    </div>

                    <div className="mt-1">
                      {formatCreditCardInfo() || 'None'}
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        )}

        <div className="mt-4">
          <div className="card">
            <div className="card-header">
              <strong>Transactions</strong>
            </div>
            <TransactionsTable />
          </div>
        </div>
      </div>

      {
        showDeletePaymentMethodConfirmationModal && (
          <DeletePaymentMethodConfirmationModal
            show={showDeletePaymentMethodConfirmationModal}
            handleClose={handleCloseDeletePaymentMethodConfirmationModal}
            servicePlanId={servicePlan && servicePlan.subscriptionId}
          />
        )
      }
      {
        showPaymentMethodModal && (
          <EditPaymentMethodModal
            show={showPaymentMethodModal}
            handleClose={handleClosePaymentMethodModal}
          />
        )
      }

      {
        showCancelModal && (
          <CancelServiceModal
            show={showCancelModal}
            handleClose={handleCloseCancelModal}
            hasPaymentMethod={
              paymentInfo && paymentInfo.hasPaymentMethod
                ? paymentInfo.hasPaymentMethod
                : false
            }
            endDate={
              servicePlan && servicePlan.renewalDate
                ? servicePlan.renewalDate
                : null
            }
            onConfirmRemovePayment={handleConfirmRemovePayment}
          />
        )
      }

      {showBillingEmailModal && (
        <BillingEmailModal
          show={showBillingEmailModal}
          handleClose={handleCloseBillingEmailModal}
          initialEmail={billingEmail}
        />
      )}

      {showServiceAddressModal && (
        <ServiceAddressModal
          show={showServiceAddressModal}
          handleClose={handleCloseServiceAddressModal}
          serviceAddress={serviceAddress}
          countryList={countryList}
          servicePlanId={servicePlan && servicePlan.subscriptionId}
        />
      )}
    </>
  );
};

BillingOverview.propTypes = {
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    crmId: PropTypes.number,
    code: PropTypes.number,
    name: PropTypes.string,
    abbreviation: PropTypes.string,
  })).isRequired,
};

export default BillingOverview;
